import React from "react";
import { Avatar, Box, Typography } from "@mui/material";
import MuiSearchableSelect from "@/components/muiCustom/select/MuiSearchableSelect";

type ParticipantOption = {
  id?: string | number;
  value?: string | number;
  name?: string;
  label?: string;
  avatar?: string | null;
};

type ParticipantFilterProps = {
  participants?: ParticipantOption[];
  value?: string | number | null;
  onChange: (_value: string | number | null) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  onListEndReached?: () => void;
};

function ParticipantFilter({
  participants = [],
  value = null,
  onChange,
  error = false,
  helperText = "",
  required = false,
  placeholder = "",
  searchPlaceholder = "Search..",
  onListEndReached,
}: ParticipantFilterProps) {
  const options = participants.map((p) => ({
    label: p.name || p.label,
    value: p.id || p.value,
    avatar: p.avatar || "",
  }));

  const selectedOption = options.find((o) => o.value === value);

  return (
    <MuiSearchableSelect
      options={options}
      value={value ?? ""}
      onChange={(_, newValue) => {
        onChange(newValue || null);
      }}
      multiple={false}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      size="small"
      error={error}
      helperText={helperText}
      required={required}
      disableClearable
      listboxProps={{
        onScroll: (event) => {
          const list = event.currentTarget;
          const isNearBottom =
            list.scrollTop + list.clientHeight >= list.scrollHeight - 24;

          if (isNearBottom) {
            onListEndReached?.();
          }
        },
      }}
      selectButtonProps={{
        "aria-label": selectedOption
          ? `Participant: ${selectedOption.label}`
          : "Search..",
      }}
      renderValue={
        selectedOption
          ? () => (
              <Box alignItems="center" display="flex" gap={0.75} minWidth={0}>
                <Avatar
                  aria-label={`${selectedOption.label} avatar`}
                  src={selectedOption.avatar}
                  sx={{
                    width: 22,
                    height: 22,
                    fontSize: "0.65rem",
                    flexShrink: 0,
                  }}
                >
                  {selectedOption.label?.[0]}
                </Avatar>
                <Typography fontSize=".8rem" fontWeight={600} noWrap>
                  {selectedOption.label}
                </Typography>
              </Box>
            )
          : undefined
      }
      renderOption={(option) => (
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <Avatar
            src={option.avatar}
            sx={{ width: 24, height: 24, fontSize: "0.7rem", flexShrink: 0 }}
          >
            {option.label?.[0]}
          </Avatar>
          <Typography fontSize=".8rem" noWrap>
            {option.label}
          </Typography>
        </Box>
      )}
    />
  );
}

export default ParticipantFilter;
